import { Query, ResultSet } from "@cubejs-client/core";
import { AppThunk } from "appThunk";
import { cubeLoad } from "modules/helpers/cube/cubeSlice";
import { logError } from "modules/helpers/logger/loggerSlice";
import { PlaceCategory } from "modules/customer/tools/location/placeCategory";
import { SizeBucket } from "modules/customer/tools/location/sizeBucket";
import { BrandClassification } from "domain/BrandClassification";

export class NearbyStore {
    public readonly retailCentreId: number;
    public readonly id: string;
    public readonly storeName: string;
    public readonly brandName: string | null;
    public readonly placeCategoryId: number;
    public readonly placeSuperCategory: string;
    public readonly sizeBucketId: number;
    public readonly sizeBucketName: string;
    public readonly latitude: number;
    public readonly longitude: number;
    public readonly brandClassification: BrandClassification;

    constructor(
        retailCentreId: number,
        id: string,
        storeName: string,
        brandName: string | null,
        placeCategoryId: number,
        placeSuperCategory: string,
        sizeBucketId: number,
        sizeBucketName: string,
        latitude: number,
        longitude: number,
        brandClassification: BrandClassification,

    ) {
        this.retailCentreId = retailCentreId;
        this.id = id;
        this.storeName = storeName;
        this.brandName = brandName;
        this.placeCategoryId = placeCategoryId;
        this.placeSuperCategory = placeSuperCategory;
        this.sizeBucketId = sizeBucketId;
        this.sizeBucketName = sizeBucketName;
        this.latitude = latitude;
        this.longitude = longitude;
        this.brandClassification = brandClassification;
    }

    get isIndependent(): boolean {
        return !this.brandName;
    }
}

export const loadNearbyStores = (retailCentreIds: number[], placeCategories: PlaceCategory[], sizeBuckets: SizeBucket[]): AppThunk<Promise<NearbyStore[]>> => async (dispatch) => {
    try {
        if (retailCentreIds.length === 0) {
            return [];
        }
        const placeCategoryMap = new Map<number, PlaceCategory>();
        placeCategories.forEach(pc => placeCategoryMap.set(pc.id, pc));

        const query: Query = {
            dimensions: [
                "Mapping_RC_RZ.RetailCentreID",
                "Place_RZ.PlaceID",
                "Place_RZ.PlaceName",
                "Place_RZ.BrandName",
                "Place_RZ.PlaceCategory_ID",
                "Place_RZ.SizeBucketId",
                "Place_RZ.Lat",
                "Place_RZ.Lng",
                "Place_RZ.PlaceClassificationName",
            ],
            filters: [{
                member: "Mapping_RC_RZ.RetailCentreID",
                operator: "equals",
                values: retailCentreIds.map(String)
            }, {
                member: "Place_RZ.PlaceID",
                operator: "set",
            }],
            segments: ["PlaceCategory.IsRetailerCategory"],
            limit: 50000
        };

        const resultSet = await dispatch(cubeLoad(query)) as unknown as ResultSet;

        const rawData = resultSet.rawData();
        const nearbyStores = rawData.map(row => {
            const placeCategoryId = Number(row["Place_RZ.PlaceCategory_ID"]);
            const superCategoryName = placeCategoryMap.get(placeCategoryId)?.superCategoryName ?? "Unknown";
            const sizeBucketLabel = sizeBuckets.find(bucket => bucket.id === Number(row["Place_RZ.SizeBucketId"]))?.label ?? "Unknown";
            return new NearbyStore(
                Number(row["Mapping_RC_RZ.RetailCentreID"]),
                String(row["Place_RZ.PlaceID"]),
                row["Place_RZ.PlaceName"],
                row["Place_RZ.BrandName"],
                placeCategoryId,
                superCategoryName,
                Number(row["Place_RZ.SizeBucketId"]),
                sizeBucketLabel,
                Number(row["Place_RZ.Lat"]),
                Number(row["Place_RZ.Lng"]),
                BrandClassification.fromString(row["Place_RZ.PlaceClassificationName"])
            );
        });
        return nearbyStores;
    } catch (error) {
        dispatch(logError("Error loading NearbyStores.", error));
        throw error;
    }
};
